// src/utils/sanitize.js
// CSS scoping and iframe-document helpers.
//
// The hand-rolled sanitizer that used to live here is gone. It stripped
// script/object/embed/form and on* handlers by parsing into a detached div and
// re-serializing, which left it open to mutation-XSS — the payload survives the
// round trip as attribute text and re-materializes as an element when the
// string is parsed a second time. DOMPurify exists to close exactly that class
// of bug, so sanitizeHTML is now a thin re-export of the shared module and
// there is one allowlist in the codebase rather than two.
//
// See utils/sanitizeHtml.ts. What remains here is scoping and serialization,
// which are not security boundaries.

import { sanitizeEmbeddedDocument } from './sanitizeHtml';

export const stripDanglingAttributeFragments = (html) => {
  if (!html) return '';
  return html
    // common case: newline + class attr fragment at line start
    .replace(/(?:^|\n)\s*class="[^"]*"\s*&gt;?\s*$/gmi, '')
    // literal > scenarios if sanitizer already converted entities
    .replace(/(?:^|\n)\s*class="[^"]*"\s*>?\s*$/gmi, '')
    // generic utility soup ending in quote+gt as raw text
    .replace(/(?:^|\n)\s*(?:[a-z0-9:_-]+\s+){2,}text-[a-z0-9:-]+["']?&gt;?\s*$/gmi, '');
};

/**
 * Sanitize HTML destined for an iframe or an export file.
 *
 * Kept as a named export so existing callers do not have to change, but the
 * work is done by the shared DOMPurify module. Content that lands in the app's
 * own DOM must use sanitizeHtml() from utils/sanitizeHtml.ts instead — this is
 * the wider, document-shaped profile.
 */
export const sanitizeHTML = (html) => {
  if (!html) return '';
  return stripDanglingAttributeFragments(sanitizeEmbeddedDocument(html));
};

/**
 * Scope HTML content to prevent CSS conflicts
 */
export const scopeHTML = (html, scopeId) => {
  if (!html) return { html: '', hasStyle: false };
  
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Check if there are style tags
  const styleTags = temp.querySelectorAll('style');
  const hasStyle = styleTags.length > 0;
  
  if (hasStyle) {
    // Scope CSS rules
    styleTags.forEach(styleTag => {
      let css = styleTag.textContent || '';
      
      // Simple CSS scoping - prefix selectors with scope ID
      css = css.replace(/([^{}]+){/g, (match, selector) => {
        // Skip @rules, keyframes, etc.
        if (selector.trim().startsWith('@')) {
          return match;
        }
        
        // Add scope to each selector
        const scopedSelector = selector
          .split(',')
          .map(s => `#${scopeId} ${s.trim()}`)
          .join(', ');
        
        return `${scopedSelector} {`;
      });
      
      styleTag.textContent = css;
    });
  }
  
  // Add scope ID to the container
  const wrapper = document.createElement('div');
  wrapper.id = scopeId;
  wrapper.innerHTML = temp.innerHTML;
  
  return {
    html: wrapper.outerHTML,
    hasStyle
  };
};

/**
 * The message a framed document sends its parent to report how tall it is.
 * Shared so both ends agree on the shape.
 */
export const FRAME_HEIGHT_MESSAGE = 'lessonplannerone:frame-height';

/**
 * Upper bound on a height a framed document may ask for. The content inside
 * the frame is untrusted, so without a clamp it could report a preposterous
 * number and blow out the page layout. Generous enough for a long uploaded
 * layout, far short of anything pathological.
 */
export const MAX_FRAME_HEIGHT = 20000;
const MIN_FRAME_HEIGHT = 40;

export const clampFrameHeight = (value) => {
  // Strict on type, not just on Number(): the payload comes from untrusted
  // framed content, and Number(null) is 0 — finite — which would quietly
  // collapse the frame instead of rejecting a malformed message.
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(Math.max(Math.ceil(value), MIN_FRAME_HEIGHT), MAX_FRAME_HEIGHT);
};

/**
 * First-party script injected into every framed document so it can report its
 * own height to the parent.
 *
 * The frames run with sandbox="allow-scripts" and WITHOUT allow-same-origin,
 * so they sit in an opaque origin: the parent can no longer reach in and read
 * contentDocument.scrollHeight. The document measures itself instead and posts
 * the number out.
 *
 * postMessage targetOrigin is '*' because a null-origin document cannot know
 * its parent's origin. That is acceptable only because the payload is a single
 * integer — never content, never anything secret. The trust check lives on the
 * receiving end, which verifies event.source is the very iframe it owns.
 *
 * The closing tag is written as a template expression so the literal
 * "</script>" never appears in this source, which would otherwise terminate a
 * surrounding <script> element if this bundle were ever inlined into a page.
 */
const FRAME_HEIGHT_REPORTER = `<script>
(function () {
  var CHANNEL = ${JSON.stringify(FRAME_HEIGHT_MESSAGE)};
  var last = -1;
  function report() {
    // Measure the body, not documentElement: documentElement.scrollHeight is
    // floored at the viewport height, which is the frame's *current* height,
    // so using it would let the frame grow but never shrink back down. The
    // body has no such floor. This matches what the old same-origin
    // measurement read.
    var body = document.body;
    var height = body
      ? Math.max(body.scrollHeight, body.offsetHeight)
      : (document.documentElement ? document.documentElement.scrollHeight : 0);
    if (height === last) return;
    last = height;
    try {
      window.parent.postMessage({ type: CHANNEL, height: height }, '*');
    } catch (e) { /* parent went away */ }
  }
  window.addEventListener('load', report);
  window.addEventListener('resize', report);
  if (typeof ResizeObserver === 'function') {
    // Late-loading images and script-driven content change the height after
    // load, so keep watching rather than measuring once.
    //
    // Watch the body, which is what report() measures. documentElement tracks
    // the frame's viewport instead, so once the parent had sized the frame it
    // stopped changing and the observer went quiet — leaving a first, wrong
    // measurement in place forever.
    try {
      var observer = new ResizeObserver(report);
      if (document.body) observer.observe(document.body);
      observer.observe(document.documentElement);
    } catch (e) { /* older engine — the load/resize handlers still fire */ }
  }
  report();
})();
</${'script'}>`;

/**
 * Build srcdoc for iframe with proper HTML structure.
 *
 * IMPORTANT — ordering: `html` must ALREADY be sanitized when it arrives here
 * (callers run it through sanitizeEmbeddedDocument first). The height reporter
 * below is appended to that finished string on purpose. It is first-party code,
 * not user content, and the strict allowlist would strip it on sight, so it
 * must never be routed back through the sanitizer. Keep the concatenation in
 * this direction: sanitize the content, THEN add our script.
 */
export const buildSrcDoc = (html) => {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    /* No internal scrollbar: the parent sizes this frame to its content, so a
       scrollbar is never what the reader wants — and it actively breaks the
       measurement. A scrollbar steals ~15px of width, and content whose height
       follows its width (a responsive SVG drawing, say) then measures shorter
       than it really is, so the frame settles too small and clips. scrollHeight
       still reports the full content height with overflow hidden. */
    html, body { overflow: hidden; }
    body {
      margin: 0;
      padding: 16px;
      font-family: system-ui, -apple-system, sans-serif;
      background: white;
    }
    * { box-sizing: border-box; }
  </style>
</head>
<body>
  ${html}
  ${FRAME_HEIGHT_REPORTER}
</body>
</html>`;
};